import { visit } from "unist-util-visit";
import {
  buildCustomerInteractionUrl,
  buildCustomerPageUrl
} from "../../helpers/customerInteractionUrl.js";
import { buildPlaybookFeedbackUrl } from "../../helpers/playbookFeedbackUrl.js";
import { encodeTranscriptSnippetMarker } from "../../helpers/transcriptSnippetLocation.js";
import { assertUnreachable } from "../../helpers/utilities.js";
function stringifyStructuredContentAsMarkdown(root) {
  return stringifyRoot(root, {});
}
function stringifyStructuredContentAsScreeningText(root, options = {}) {
  return stringifyRoot(root, {
    ...options,
    screeningText: true
  });
}
function stringifyStructuredContentAsModelInput(root, options = {}) {
  return stringifyRoot(root, { modelInput: options });
}
const LIST_ITEM_INDENT = "    ";
function stringifyRoot(root, options) {
  const state = { text: "", options };
  root.children.forEach((child, childIndex) => {
    if (childIndex > 0) {
      appendRawText(state, "\n\n");
    }
    switch (child.type) {
      case "heading": {
        const wrapper = options.screeningText ? "" : "**";
        appendRawText(state, wrapper);
        appendPhrasingRun(state, child.children);
        appendRawText(state, wrapper);
        break;
      }
      case "list":
        child.children.forEach((item, itemIndex) => {
          if (itemIndex > 0) {
            appendRawText(state, "\n");
          }
          appendRawText(
            state,
            options.screeningText ? "" : LIST_ITEM_INDENT.repeat(item.depth ?? 0) + (item.number === void 0 ? "- " : `${item.number}. `)
          );
          appendPhrasingRun(state, item.children);
        });
        break;
      case "paragraph":
        appendPhrasingRun(state, child.children);
        break;
      case "table":
        appendTable(state, child);
        break;
      case "thematicBreak":
        if (!options.screeningText) {
          appendRawText(state, "---");
        }
        break;
      default:
        assertUnreachable(child, () => void 0);
    }
  });
  return state.text;
}
function appendTable(state, table) {
  const [headerRow, ...bodyRows] = table.children;
  if (!headerRow) {
    return;
  }
  const columnCount = Math.max(
    ...table.children.map((row) => row.children.length)
  );
  const appendRow = (row) => {
    appendRawText(state, "| ");
    for (let index = 0; index < columnCount; index++) {
      if (index > 0) {
        appendRawText(state, " | ");
      }
      const cell = row.children[index];
      if (cell) {
        appendPhrasingRun(state, cell.children, {
          escapePipes: !state.options.screeningText
        });
      }
    }
    appendRawText(state, " |");
  };
  appendRow(headerRow);
  if (!state.options.screeningText) {
    const delimiterCells = Array.from({ length: columnCount }, (_, index) => {
      switch (table.align?.[index]) {
        case "left":
          return ":---";
        case "right":
          return "---:";
        case "center":
          return ":---:";
        default:
          return "---";
      }
    });
    appendRawText(state, "\n| ");
    appendRawText(state, delimiterCells.join(" | "));
    appendRawText(state, " |");
  }
  for (const row of bodyRows) {
    appendRawText(state, "\n");
    appendRow(row);
  }
}
function stringifyStructuredContentPhrasingRunAsMarkdown(content) {
  const state = { text: "", options: {} };
  appendPhrasingRun(state, content);
  return state.text;
}
function stringifyStructuredContentPhrasingAsMarkdown(content) {
  const state = { text: "", options: {} };
  appendPhrasingNode(state, content);
  return state.text;
}
function appendPhrasingRun(state, content, options = { escapePipes: false }) {
  for (const node of content) {
    appendPhrasingNode(state, node, options);
  }
}
function appendPhrasingNode(state, content, options = { escapePipes: false }) {
  switch (content.type) {
    case "text":
      appendTextNode(state, content, options);
      return;
    case "emphasis": {
      const wrapper = state.options.screeningText ? "" : "*";
      appendRawText(state, wrapper);
      appendPhrasingRun(state, content.children, options);
      appendRawText(state, wrapper);
      return;
    }
    case "strong": {
      const wrapper = state.options.screeningText ? "" : "**";
      appendRawText(state, wrapper);
      appendPhrasingRun(state, content.children, options);
      appendRawText(state, wrapper);
      return;
    }
    case "highlight":
      if (!state.options.screeningText) {
        appendRawText(state, `<${content.color}>`);
      }
      appendPhrasingRun(state, content.children, options);
      if (!state.options.screeningText) {
        appendRawText(state, `</${content.color}>`);
      }
      return;
    case "interaction_reference":
    case "grading_reference": {
      const modelInput = !state.options.screeningText && state.options.modelInput !== void 0;
      const interactionId = modelInput ? resolveModelInteractionId(content, state.options) : void 0;
      if (modelInput && interactionId) {
        appendRawText(state, `<interaction interaction_id="${interactionId}">`);
      }
      if (modelInput && content.type === "grading_reference") {
        appendRawText(state, "<customer>");
      }
      appendPhrasingRun(state, content.children, options);
      if (modelInput && content.type === "grading_reference") {
        appendRawText(state, "</customer>");
      }
      if (modelInput && interactionId) {
        appendRawText(state, "</interaction>");
      }
      return;
    }
    case "customer_reference": {
      const modelInput = !state.options.screeningText && state.options.modelInput !== void 0;
      if (modelInput) {
        appendRawText(
          state,
          `<customer${content.accountId ? ` id="${content.accountId}"` : ""}>`
        );
      }
      appendPhrasingRun(state, content.children, options);
      if (modelInput) {
        appendRawText(state, "</customer>");
      }
      return;
    }
    case "teammate_group_reference": {
      const modelInput = !state.options.screeningText && state.options.modelInput !== void 0;
      if (modelInput) {
        appendRawText(state, `<teammate-group id="${content.groupId}">`);
      }
      appendPhrasingRun(state, content.children, options);
      if (modelInput) {
        appendRawText(state, "</teammate-group>");
      }
      return;
    }
    case "teammate_reference": {
      const modelInput = state.options.screeningText ? void 0 : state.options.modelInput;
      if (modelInput) {
        appendRawText(state, `<teammate id="${content.teammateId}">`);
      }
      appendPhrasingRun(state, content.children, options);
      if (modelInput) {
        appendRawText(state, "</teammate>");
      }
      return;
    }
    case "boilerplate": {
      const shouldEmitBoilerplateTag = !state.options.screeningText && state.options.modelInput !== void 0;
      if (shouldEmitBoilerplateTag) {
        appendRawText(state, "<boilerplate>");
      }
      appendPhrasingRun(state, content.children, options);
      if (shouldEmitBoilerplateTag) {
        appendRawText(state, "</boilerplate>");
      }
      return;
    }
    case "person_reference": {
      const shouldEmitPersonTag = !state.options.screeningText && state.options.modelInput !== void 0;
      if (shouldEmitPersonTag) {
        appendRawText(state, "<person>");
      }
      appendPhrasingRun(state, content.children, options);
      if (shouldEmitPersonTag) {
        appendRawText(state, "</person>");
      }
      return;
    }
    case "date_reference": {
      const shouldEmitDateTag = !state.options.screeningText && state.options.modelInput !== void 0;
      if (shouldEmitDateTag) {
        appendRawText(state, `<date type="${content.dateType}">`);
      }
      appendPhrasingRun(state, content.children, options);
      if (shouldEmitDateTag) {
        appendRawText(state, "</date>");
      }
      return;
    }
    case "playbook_feedback_reference": {
      const shouldEmitTag = !state.options.screeningText && state.options.modelInput !== void 0;
      if (shouldEmitTag) {
        appendRawText(
          state,
          `<playbook-feedback playbook_id="${content.playbookId}" node_id="${content.nodeId}" feedback_id="${content.feedbackId}">`
        );
      }
      appendPhrasingRun(state, content.children, options);
      if (shouldEmitTag) {
        appendRawText(state, "</playbook-feedback>");
      }
      return;
    }
    case "quote": {
      const interactionId = state.options.screeningText ? void 0 : resolveModelInteractionId(content, state.options);
      if (interactionId) {
        const speakerAttribute = content.speaker ? ` speaker="${content.speaker}"` : "";
        const sourceAttribute = content.transcriptSnippetLocation ? ` source="${encodeTranscriptSnippetMarker(content.transcriptSnippetLocation)}"` : "";
        appendRawText(
          state,
          `<quote interaction_id="${interactionId}"${speakerAttribute}${sourceAttribute}>`
        );
      } else if (!state.options.screeningText) {
        appendRawText(state, '"');
      }
      appendPhrasingRun(state, content.children, options);
      if (interactionId) {
        appendRawText(state, "</quote>");
      } else if (!state.options.screeningText) {
        appendRawText(state, '"');
      }
      return;
    }
    default:
      return assertUnreachable(content, () => void 0);
  }
}
function appendRawText(state, value) {
  state.text += value;
}
function appendTextNode(state, node, options = { escapePipes: false }) {
  const outputStart = state.text.length;
  if (options.escapePipes) {
    appendRawText(state, node.value.replaceAll("|", "\\|"));
  } else {
    appendRawText(state, node.value);
  }
  state.options.onStringifyText?.(node, outputStart, state.text.length);
}
function resolveModelInteractionId(node, options) {
  if (!options.modelInput) {
    return void 0;
  }
  if (node.gradingId) {
    return node.gradingId;
  }
  const legacyKey = getLegacyGradingKey(node);
  return legacyKey ? options.modelInput.resolveLegacyGradingId?.(
    legacyKey.teammateId,
    legacyKey.accountId
  ) : void 0;
}
function stringifyStructuredContentPhrasingRunAsPlainText(content) {
  return content.map(stringifyStructuredContentPhrasingAsPlainText).join("");
}
function stringifyStructuredContentPhrasingAsPlainText(content) {
  switch (content.type) {
    case "text":
      return content.value;
    case "emphasis":
    case "strong":
    case "highlight":
    case "interaction_reference":
    case "customer_reference":
    case "boilerplate":
    case "grading_reference":
    case "teammate_group_reference":
    case "teammate_reference":
    case "person_reference":
    case "date_reference":
    case "playbook_feedback_reference":
    case "quote":
      return stringifyStructuredContentPhrasingRunAsPlainText(content.children);
    default:
      return assertUnreachable(content, () => "");
  }
}
const NOTIFICATION_TEXT_MAX_LENGTH = 200;
function summarizeStructuredContentAsNotificationText(root) {
  const opening = root.children.find((child) => child.type === "paragraph");
  const text = opening ? stringifyStructuredContentPhrasingRunAsPlainText(opening.children).trim() : "";
  if (text.length <= NOTIFICATION_TEXT_MAX_LENGTH) {
    return text;
  }
  const head = text.slice(0, NOTIFICATION_TEXT_MAX_LENGTH);
  const lastWordBreak = head.lastIndexOf(" ");
  return `${lastWordBreak > 0 ? head.slice(0, lastWordBreak) : head}\u2026`;
}
const TRANSCRIPT_QUOTE_ANCHOR_MAX_LENGTH = 200;
function buildCitationUrl(node, options, transcriptQuote) {
  if (!node.gradingId || !node.playbookId) {
    return void 0;
  }
  const path = buildCustomerInteractionUrl({
    playbookId: node.playbookId,
    gradingId: node.gradingId,
    target: transcriptQuote !== void 0 ? {
      tab: "transcript",
      quote: buildTranscriptQuoteAnchor(transcriptQuote)
    } : void 0
  });
  return `${options.appBaseUrl}${path}`;
}
function buildCustomerReferenceUrl(node, options) {
  if (!node.accountId || !node.playbookId) {
    return void 0;
  }
  const path = buildCustomerPageUrl({
    playbookId: node.playbookId,
    accountId: node.accountId
  });
  return `${options.appBaseUrl}${path}`;
}
function buildPlaybookFeedbackReferenceUrl(node, options) {
  const path = buildPlaybookFeedbackUrl({
    playbookId: node.playbookId,
    nodeId: node.nodeId,
    feedbackId: node.feedbackId
  });
  return `${options.appBaseUrl}${path}`;
}
function buildTranscriptQuoteAnchor(quote) {
  if (quote.length <= TRANSCRIPT_QUOTE_ANCHOR_MAX_LENGTH) {
    return quote;
  }
  const anchor = quote.slice(0, TRANSCRIPT_QUOTE_ANCHOR_MAX_LENGTH);
  const lastWordBreak = anchor.lastIndexOf(" ");
  return lastWordBreak > 0 ? anchor.slice(0, lastWordBreak) : anchor;
}
function collectStructuredContentCustomerAccountIds(root) {
  const accountIds = /* @__PURE__ */ new Set();
  visit(root, "customer_reference", (node) => {
    if (node.accountId) {
      accountIds.add(node.accountId);
    }
  });
  return [...accountIds];
}
function collectGradingNodesInStructuredContent(root) {
  const nodes = [];
  visit(
    root,
    ["grading_reference", "interaction_reference", "quote"],
    (node) => {
      if (node.type !== "grading_reference" && node.type !== "interaction_reference" && node.type !== "quote") {
        return;
      }
      if (!node.gradingId && !getLegacyGradingKey(node)) {
        return;
      }
      nodes.push(node);
    }
  );
  return nodes;
}
function getLegacyGradingKey(node) {
  if (node.type === "interaction_reference" || !node.accountId) {
    return void 0;
  }
  return { teammateId: node.teammateId, accountId: node.accountId };
}
function collectQuotesInStructuredContent(root) {
  const nodes = [];
  visit(root, "quote", (node) => {
    if (node.type === "quote") {
      nodes.push(node);
    }
  });
  return nodes;
}
export {
  buildCitationUrl,
  buildCustomerReferenceUrl,
  buildPlaybookFeedbackReferenceUrl,
  collectGradingNodesInStructuredContent,
  collectQuotesInStructuredContent,
  collectStructuredContentCustomerAccountIds,
  getLegacyGradingKey,
  stringifyStructuredContentAsMarkdown,
  stringifyStructuredContentAsModelInput,
  stringifyStructuredContentAsScreeningText,
  stringifyStructuredContentPhrasingAsMarkdown,
  stringifyStructuredContentPhrasingRunAsMarkdown,
  stringifyStructuredContentPhrasingRunAsPlainText,
  summarizeStructuredContentAsNotificationText
};
//# sourceMappingURL=helpers.js.map
